import {
    computed,
    type DefineComponent,
    type FunctionalComponent,
    h,
    inject,
    mergeProps,
    onBeforeMount,
    onUnmounted,
    provide,
    type Ref,
    ref,
    type Slots,
    useSlots,
    type WatchOptions,
    type MaybeRefOrGetter,
    toValue,
} from 'vue';
import { defaultDivisibleRemote, loading } from './symbols';
import type {
    DivisibleInjectionKey,
    DivisiblePart,
    DivisibleRemote,
} from './types';
import { DivisibleReceiver, emptyRemote } from './DivisibleReceiver';
import './DivisibleReceiver.augmented';
import type { Observable, ObservableInput } from 'rxjs';
import { from } from '@vueuse/rxjs';

/**
 * Creates a new `DynamicReceiver` object and injects it's remote using the provided key. It will also automatically
 * close the receiver when the parent component is unmounted.
 *
 * Typically, you will use this composable in receiver components.
 *
 * @param key The key to inject, default to `defaultDynamicRemote`.
 */
export function useDivisibleReceiver(key?: DivisibleInjectionKey) {
    const receiver = new DivisibleReceiver();

    onBeforeMount(() =>
        provide(key ?? defaultDivisibleRemote, receiver.remote())
    );
    onUnmounted(() => receiver.close());

    return receiver;
}

/**
 * Injects the `DynamicRemote` using the provided injection key. It will give an empty (unconnected) remote if no
 * remote was provided with that key.
 *
 * This composable can be used for low-level access of the `DynamicRemote` in Dynamic components.
 *
 * @param key The key at which `useDynamicReceiver` injected its remote. Defaults to `defaultDivisibleRemote`.
 */
export function useDivisibleRemote(key?: DivisibleInjectionKey) {
    return inject<Readonly<DivisibleRemote>>(
        key ?? defaultDivisibleRemote,
        emptyRemote,
        true
    );
}

/**
 * Injects the `DynamicRemote` using the `useDynamicRemote`-composable and defines some convenience functions
 * to work with it.
 *
 * This composable is used in dynamic components.
 *
 * @param key The key at which `useDynamicReceiver` injected its remote. Defaults to `defaultDivisibleRemote`.
 */
export function useDivisible(key?: DivisibleInjectionKey) {
    const connector = useDivisibleRemote(key);
    const {
        give: giveObservable,
        keys: targetKeys,
        has: hasTarget,
    } = connector;

    function give(target: string, observable: Observable<DivisiblePart>) {
        const observed = ref<boolean>(true);
        const subscription = giveObservable(target, observable);
        subscription.add(() => (observed.value = false));
        return { observed, subscription };
    }

    function giveFrom(
        target: string,
        part: ObservableInput<DivisiblePart> | Ref<DivisiblePart>,
        options?: WatchOptions
    ) {
        return { ...give(target, from(part, options)), part };
    }

    const slots = useSlots();
    function giveSlot(
        target: string,
        options: {
            slotKey?: MaybeRefOrGetter<string>;
            slotProps?: MaybeRefOrGetter<Record<string, any> | undefined>;
            fallback?: MaybeRefOrGetter<string | null | undefined>;
            show?: MaybeRefOrGetter<boolean>;
            loading?: MaybeRefOrGetter<boolean>;
        } = {}
    ) {
        const part = computed<DivisiblePart>(() => {
            if (!toValue(options.show)) return null;
            if (toValue(options.loading)) return loading;
            const key = toValue(options.slotKey) ?? target;
            const slot = slots[key];
            if (slot)
                return (props) =>
                    slot(mergeProps(toValue(options.slotProps) ?? {}, props));
            return toValue(options.fallback) ?? null;
        });
        return {
            ...giveFrom(target, part, { immediate: true, deep: false }),
            part,
        };
    }

    function giveComponent(
        target: string,
        component: MaybeRefOrGetter<
            FunctionalComponent | DefineComponent | null
        >,
        componentProps?: MaybeRefOrGetter<Record<string, any> | undefined>,
        componentSlots?: MaybeRefOrGetter<Slots>
    ) {
        const part = computed<FunctionalComponent | null>(() => {
            const c = toValue(component);
            if (!c) return null;
            return (props, { slots }) =>
                h(c as any, mergeProps(toValue(componentProps) ?? {}, props), {
                    ...componentSlots,
                    ...slots,
                });
        });
        return {
            ...giveFrom(target, part, { immediate: true, deep: false }),
            part,
        };
    }

    function givePart(target: string) {
        console.warn('givePart not implemented yet.', { target });
    }

    return {
        give: Object.assign(give, {
            component: giveComponent,
            slot: giveSlot,
            from: giveFrom,
            part: givePart,
        }),
        giveComponent,
        giveObservable,
        giveSlot,
        giveFrom,
        targetKeys,
        hasTarget,
        connector,
    };
}
